import { useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { ADMIN_MANY_ORDERS_API_PATHS } from '../constants/orders-api-paths.constant';

const DEFAULT_ERROR_MESSAGE = 'Une erreur est survenue lors du téléchargement du fichier CSV';

interface DownloadDeliveryNotesCsvState {
  downloading: boolean;
  downloadCsv: (deliveryDate: string) => Promise<void>;
}

async function extractApiErrorMessage(error: unknown): Promise<string | undefined> {
  const responseData = (error as { response?: { data?: unknown } }).response?.data;

  if (responseData instanceof Blob) {
    try {
      const parsedBody = JSON.parse(await responseData.text());
      return typeof parsedBody.message === 'string' ? parsedBody.message : undefined;
    } catch {
      return undefined;
    }
  }

  if (responseData && typeof responseData === 'object' && 'message' in responseData) {
    const { message } = responseData as { message: unknown };
    return typeof message === 'string' ? message : undefined;
  }

  return undefined;
}

function triggerBrowserDownload(csvBlob: Blob, filename: string): void {
  const blobUrl = window.URL.createObjectURL(csvBlob);
  const downloadLink = document.createElement('a');
  downloadLink.href = blobUrl;
  downloadLink.download = filename;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();
  window.URL.revokeObjectURL(blobUrl);
}

export function useDownloadDeliveryNotesCsv(): DownloadDeliveryNotesCsvState {
  const { enqueueSnackbar } = useSnackbar();
  const [downloading, setDownloading] = useState(false);

  const downloadCsv = async (deliveryDate: string): Promise<void> => {
    setDownloading(true);

    try {
      const csvBlob = await HttpClient.get<Blob>(ADMIN_MANY_ORDERS_API_PATHS.GET_ORDERS_DELIVERY_NOTES_CSV, {
        params: { deliveryDate },
        responseType: 'blob',
      });

      triggerBrowserDownload(csvBlob, `bons-de-livraison_${deliveryDate}.csv`);
    } catch (error) {
      const message = (await extractApiErrorMessage(error)) ?? DEFAULT_ERROR_MESSAGE;
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  return { downloading, downloadCsv };
}
